import { ArrowRight, Sparkles } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden bg-white pt-28 md:pt-32 pb-16 md:pb-24"
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.35]">
        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#0A3B2C]/5 blur-3xl" />
        <div className="absolute top-1/2 -left-32 h-[400px] w-[400px] rounded-full bg-[#10B981]/5 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left content */}
          <div className="fd-fade-up">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0F5F2] border border-[#0A3B2C]/10 text-[#0A3B2C] text-xs font-semibold uppercase tracking-[0.18em] mb-6"
              data-testid="hero-overline"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Direktvertrieb für regionale Versorger
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0A3B2C] mb-6"
              data-testid="hero-headline"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Wir machen regionale{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Versorger</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 bg-[#10B981]/20 -z-0" />
              </span>{" "}
              wieder sichtbar.
            </h1>

            <p className="text-lg md:text-xl text-gray-700 font-medium mb-4" data-testid="hero-subheadline">
              Direktvertrieb mit Struktur, Qualität und regionaler Stärke.
            </p>

            <p className="text-base text-gray-600 leading-relaxed max-w-xl mb-8" data-testid="hero-description">
              faktor-direkt unterstützt Stadtwerke, Gemeindewerke und regionale
              Energieversorger bei Kundenrückgewinnung, Bestandskundenpflege,
              Neukundengewinnung und der Stärkung ihrer regionalen Marktposition.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => scrollTo("#kontakt")}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 bg-[#0A3B2C] text-white hover:bg-[#072A1F] px-7 py-3.5 font-semibold rounded-md text-sm shadow-sm hover:shadow-md transition-all"
              >
                Potenzialgespräch anfragen
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={() => scrollTo("#leistungen")}
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center bg-white text-[#0A3B2C] border border-[#0A3B2C]/20 hover:border-[#0A3B2C] hover:bg-[#F9FAFB] px-7 py-3.5 font-semibold rounded-md text-sm transition-all"
              >
                Mehr erfahren
              </button>
            </div>

            {/* Trust bar */}
            <div className="mt-10 pt-6 border-t border-gray-100 grid grid-cols-3 gap-4 max-w-md">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-[#0A3B2C]" style={{ fontFamily: "Outfit, sans-serif" }}>10+</div>
                <div className="text-xs text-gray-500 mt-1">Jahre Erfahrung</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-[#0A3B2C]" style={{ fontFamily: "Outfit, sans-serif" }}>10k+</div>
                <div className="text-xs text-gray-500 mt-1">Verträge</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-[#0A3B2C]" style={{ fontFamily: "Outfit, sans-serif" }}>100%</div>
                <div className="text-xs text-gray-500 mt-1">Regional</div>
              </div>
            </div>
          </div>

          {/* Right - founder images composition */}
          <div className="relative fd-fade-up fd-delay-2">
            <div className="relative grid grid-cols-2 gap-3 md:gap-4">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-xl bg-gray-100 relative">
                <img
                  src={ASSETS.founders.hisen}
                  alt="Hisen Kajtazi"
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ objectPosition: "55% 45%", transform: "scale(1.3)", transformOrigin: "55% 65%" }}
                  data-testid="founder-image-hisen"
                />
              </div>
              <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-xl bg-gray-100 relative">
                <img
                  src={ASSETS.founders.anil}
                  alt="Anil Dogan"
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ objectPosition: "50% 25%", transform: "scale(1.05)", transformOrigin: "50% 25%" }}
                  data-testid="founder-image-anil"
                />
              </div>
            </div>

            {/* Overlapping founder cards */}
            <div className="mt-4 md:absolute md:-bottom-8 md:left-1/2 md:-translate-x-1/2 md:w-[110%] grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <FounderCard
                testId="founder-card-hisen"
                name="Hisen Kajtazi"
                role="Vertrieb & operative Umsetzung"
                text="Über 10 Jahre Door-to-Door-Erfahrung, Führungserfahrung und mehr als 10.000 geschriebene Verträge."
              />
              <FounderCard
                testId="founder-card-anil"
                name="Anil Dogan"
                role="Strategie, Qualität & Organisation"
                text="Experte für Unternehmensorganisation, Ausbildung, Qualitätssicherung, Führung und wirtschaftliche Struktur."
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FounderCard({ testId, name, role, text }) {
  return (
    <div
      data-testid={testId}
      className="bg-[#0A3B2C] text-white rounded-xl p-5 shadow-2xl border border-white/10 backdrop-blur-sm"
    >
      <div className="text-lg font-bold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
        {name}
      </div>
      <div className="text-xs uppercase tracking-widest text-[#10B981] mt-1 mb-3 font-semibold">
        {role}
      </div>
      <p className="text-sm text-white/80 leading-relaxed">{text}</p>
    </div>
  );
}
